import React, { useEffect, useState } from "react";
import { startOfDay, endOfDay } from "date-fns";
import styles from "./ExportModal.module.css";
import ReactModal from "react-modal";
import Icon from "../Icon/Icon";
import SelectElement, {
  type SelectListItemType,
} from "../SelectElement/SelectElement";
import Calendar from "../Calendar/Calendar";
import { type IGetAllDocumentsTypesResponse } from "../../shared/types/templates.types";
import api from "../../api/api";

type CreatePropertyModalProps = {
  isOpen: boolean;
  onRequestClose: () => void;
  selectedIds: string[];
};

const ExportModal = ({
  isOpen,
  onRequestClose,
  selectedIds,
}: CreatePropertyModalProps) => {
  const [selectedDateFrom, setSelectedDateFrom] = useState<Date | undefined>();
  const [selectedDateTo, setSelectedDateTo] = useState<Date | undefined>();
  const [selectedOption, setSelectedOption] = useState<SelectListItemType>({
    id: "Tipo de Documento",
    element: <p className={styles.selectedItem}>Tipo de Documento</p>,
  });

  const [templates, setTemplates] =
    useState<IGetAllDocumentsTypesResponse | null>(null);
  const [filteredTemplates, setFilteredTemplates] =
    useState<IGetAllDocumentsTypesResponse | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<
    { id: string; name: string }[] | null
  >(null);

  useEffect(() => {
    const fetchTemplates = async (ids: string[]) => {
      try {
        const response = await api.getAllDocumentsTypes();
        console.log(ids);

        if (ids.length !== 0) {
          const selectedTypes = response.filter((type) =>
            ids.includes(type.id)
          );
          setTemplates(selectedTypes);
          setFilteredTemplates(selectedTypes);
          setSelectedTypes(selectedTypes);
          return;
        }

        setTemplates(response);
        setFilteredTemplates(response);
        setSelectedTypes(response);
      } catch (error) {
        console.log(error);
      }
    };

    if (isOpen) {
      fetchTemplates(selectedIds);
    }
  }, [isOpen, selectedIds]);

  // Фильтрация по дате
  useEffect(() => {
    if (!templates) return;

    const filtered = templates.filter((template) => {
      const createdAt = new Date(template.createdAt);

      const from = selectedDateFrom ? startOfDay(selectedDateFrom) : undefined;
      const to = selectedDateTo ? endOfDay(selectedDateTo) : undefined;

      if (from && createdAt < from) return false;
      if (to && createdAt > to) return false;

      return true;
    });

    setFilteredTemplates(filtered);

    const stillValid = filtered.some((item) => item.id === selectedOption.id);

    if (!stillValid) {
      setSelectedOption({
        id: "Tipo de Documento",
        element: <p className={styles.selectedItem}>Tipo de Documento</p>,
      });
      setSelectedTypes(filtered);
    } else if (selectedOption.id === "Tipo de Documento") {
      setSelectedTypes(filtered);
    }
  }, [templates, selectedDateFrom, selectedDateTo, selectedOption.id]);

  const selectOption = (option: SelectListItemType) => {
    setSelectedOption(option);
    if (option.name) {
      setSelectedTypes([{ id: option.id, name: option.name }]);
    } else {
      setSelectedTypes(filteredTemplates);
    }
    setSelectedDateFrom(undefined);
    setSelectedDateTo(undefined);
  };

  const removeType = (id: string) => {
    if (!selectedTypes) return;
    const updatedTypes = selectedTypes.filter((type) => type.id !== id);
    setSelectedTypes(updatedTypes);
  };

  const handleExportData = async () => {
    if (!selectedTypes) return;
    const blob = await api.exportTemplatesToExcel({
      ids: selectedTypes.map((type) => type.id).join(", "),
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "document-types.xlsx";
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleExportData();
    closeModal();
  };

  const mappedTemplates =
    filteredTemplates?.map((template) => {
      return {
        id: template.id,
        element: <p className={styles.selectListItem}>{template.name}</p>,
        name: template.name,
      };
    }) || [];

  const options: SelectListItemType[] = [
    {
      id: "Tipo de Documento",
      element: <p className={styles.selectListItem}>Tipo de Documento</p>,
    },
    ...mappedTemplates,
  ];

  const filteredOptions = options.filter(
    (option) => selectedOption.id !== option.id
  );

  const closeModal = () => {
    onRequestClose();
    setSelectedDateFrom(undefined);
    setSelectedDateTo(undefined);
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={closeModal}
      className={styles.modal}
      overlayClassName={styles.overlay}
      ariaHideApp={false}
    >
      <h2 className={styles.title}>
        Descargar fichero <Icon name="download" size={32} />
      </h2>
      <p className={styles.subtitle}>
        Se descargara un fichero con los documentos filtrados. Este fichero
        contiene todas las propiedades asignadas a estos documentos
      </p>

      <form onSubmit={handleSubmit} className={styles.form}>
        <label className={styles.label}>
          Agregar documento
          <SelectElement
            options={filteredOptions}
            selected={selectedOption}
            setSelected={selectOption}
            className={styles.selectElement}
            dropdownItemClassName={styles.selectElementDropdownItem}
            dropdownClassName={styles.selectElementDropdown}
          />
        </label>
        <div className={styles.label}>
          Se descargaran ficheros de
          <ul className={styles.typesList}>
            {selectedTypes &&
              selectedTypes?.length > 0 &&
              selectedTypes.map((type) => (
                <li key={type.id} className={styles.typesListItem}>
                  {type.name}
                  <button
                    className={styles.deleteTypeIcon}
                    type="button"
                    onClick={() => removeType(type.id)}
                  >
                    <Icon name="x-mark" size={22} color="#ffffff" />
                  </button>
                </li>
              ))}
          </ul>
        </div>
        <div className={styles.label}>
          Selecciona el periodo de tiempo
          <div className={styles.datePickersContainer}>
            <div className={styles.datePicker}>
              Desde
              <Calendar
                maxDate={selectedDateTo}
                wraperClassName={styles.datePickerWrapper}
                pickerClassName={styles.picker}
                selectedDate={selectedDateFrom}
                setSelectedDate={setSelectedDateFrom}
              />
            </div>
            <div className={styles.datePicker}>
              Hasta
              <Calendar
                minDate={selectedDateFrom}
                wraperClassName={styles.datePickerWrapper}
                pickerClassName={styles.picker}
                selectedDate={selectedDateTo}
                setSelectedDate={setSelectedDateTo}
              />
            </div>
          </div>
        </div>
        <div className={styles.buttonsContainer}>
          <button className={styles.cancelButton} onClick={closeModal}>
            Cancelar
          </button>
          <button
            type="submit"
            className={styles.submitButton}
            disabled={selectedTypes?.length === 0}
          >
            Descargar Ficheros <Icon name="stars" size={20} />
          </button>
        </div>
      </form>
      <button className={styles.closeIcon} onClick={closeModal}>
        <Icon name="x-mark" size={16} />
      </button>
    </ReactModal>
  );
};

export default ExportModal;
